<template>
  <b-modal
    v-model="visible"
    centered
    hide-footer
    size="xl"
    :title="messages.IMPORT_WIZARD_TITLE"
    modal-class="operaciones-import-wizard"
    :no-close-on-backdrop="submitting"
    :no-close-on-esc="submitting"
    @hide="handleHide"
    @hidden="handleHidden"
  >
    <div v-if="step === 'select'">
      <p class="text-grey">{{ messages.IMPORT_STEP_SELECT_TITLE }}</p>
      <p class="text-muted small">
        La plantilla debe tener las columnas RUC, Tienda, Material, OC y MIGO.
        El sistema empareja cada fila por RUC + Tienda + Material contra el
        periodo seleccionado.
      </p>

      <div class="d-flex flex-wrap">
        <b-button class="bg-grey-btn mr-2 mb-2" @click="downloadTemplate">
          <font-awesome-icon icon="file-excel" />
          {{ messages.DOWNLOAD_TEMPLATE_BUTTON }}
        </b-button>

        <b-button
          class="bg-orange-btn mb-2"
          :disabled="reading"
          @click="triggerFileSelect"
        >
          <b-spinner v-if="reading" small class="mr-1" />
          <font-awesome-icon v-else icon="upload" />
          {{
            reading ? messages.IMPORT_VALIDATING : messages.SELECT_FILE_BUTTON
          }}
        </b-button>
        <input
          ref="fileInput"
          type="file"
          accept=".xlsx,.xls"
          class="d-none"
          @change="onFileSelected"
        />
      </div>

      <b-alert v-if="readError" show variant="danger" class="mt-3">
        {{ readError }}
      </b-alert>
    </div>

    <div v-else-if="step === 'preview'">
      <p class="text-grey mb-2">{{ messages.IMPORT_STEP_PREVIEW_TITLE }}</p>

      <div class="mb-3 d-flex flex-wrap align-items-center">
        <b-badge variant="success" class="mr-2">
          {{ validRows.length }} {{ messages.IMPORT_VALID_ROWS_LABEL }}
        </b-badge>
        <b-badge v-if="invalidRows.length" variant="danger" class="mr-3">
          {{ invalidRows.length }} {{ messages.IMPORT_INVALID_ROWS_LABEL }}
        </b-badge>

        <b-form-radio-group
          v-if="invalidRows.length"
          v-model="rowFilter"
          size="sm"
          buttons
          button-variant="outline-secondary"
          class="mr-3"
        >
          <b-form-radio value="error"
            >Solo con error ({{ invalidRows.length }})</b-form-radio
          >
          <b-form-radio value="valid"
            >Válidas ({{ validRows.length }})</b-form-radio
          >
          <b-form-radio value="all">Todas ({{ rows.length }})</b-form-radio>
        </b-form-radio-group>

        <span class="text-muted">{{ fileName }}</span>
      </div>

      <b-alert
        v-if="errorSummary.length"
        show
        variant="warning"
        class="mb-3 py-2"
      >
        <div v-for="entry in errorSummary" :key="entry.message" class="small">
          <strong>{{ entry.count }}</strong> — {{ entry.message }}
        </div>
      </b-alert>

      <b-alert v-if="validationError" show variant="warning" class="mb-3">
        {{ validationError }}
      </b-alert>

      <div class="operaciones-import-wizard__table-wrap">
        <b-table
          small
          responsive
          :items="filteredRows"
          :fields="previewFields"
          :tbody-tr-class="previewRowClass"
          :busy="validating"
          :per-page="perPage"
          :current-page="currentPage"
          show-empty
          empty-text="No hay filas que coincidan con este filtro."
        >
          <template #table-busy>
            <div class="text-center text-muted my-3">
              <b-spinner small class="align-middle mr-2" />
              {{ messages.IMPORT_VALIDATING }}
            </div>
          </template>
          <template #cell(ruc)="{ item }">
            <b-form-input
              size="sm"
              v-model="item.ruc"
              @change="revalidate(item)"
            />
          </template>
          <template #cell(tienda)="{ item }">
            <b-form-input
              size="sm"
              v-model="item.tienda"
              @change="revalidate(item)"
            />
          </template>
          <template #cell(material)="{ item }">
            <b-form-input
              size="sm"
              v-model="item.material"
              @change="revalidate(item)"
            />
          </template>
          <template #cell(numeroOc)="{ item }">
            <b-form-input
              size="sm"
              v-model="item.numeroOc"
              @change="revalidate(item)"
            />
          </template>
          <template #cell(migo)="{ item }">
            <b-form-input
              size="sm"
              v-model="item.migo"
              @change="revalidate(item)"
            />
          </template>
          <template #cell(error)="{ item }">
            <span v-if="item.error" class="text-danger small">{{
              item.error
            }}</span>
            <span v-else class="text-success small">OK</span>
          </template>
          <template #cell(acciones)="{ item }">
            <b-button
              size="sm"
              variant="link"
              class="text-danger p-0"
              @click="removeRow(item)"
            >
              {{ messages.IMPORT_REMOVE_ROW }}
            </b-button>
          </template>
        </b-table>
        <b-pagination
          v-if="filteredRows.length > perPage"
          v-model="currentPage"
          :total-rows="filteredRows.length"
          :per-page="perPage"
          size="sm"
          align="center"
          class="mt-2 mb-0"
        />
      </div>

      <b-alert v-if="submitError" show variant="danger" class="mt-2">
        {{ submitError }}
      </b-alert>

      <div class="d-flex justify-content-between mt-3">
        <div>
          <b-button
            class="bg-grey-btn mr-2"
            :disabled="submitting"
            @click="reset"
          >
            {{ messages.IMPORT_BACK_BUTTON }}
          </b-button>
          <b-button
            class="bg-grey-btn"
            :disabled="submitting || validating"
            @click="validateRows"
          >
            <b-spinner v-if="validating" small class="mr-1" />
            {{ messages.REVALIDATE_BUTTON }}
          </b-button>
        </div>
        <b-button
          class="bg-orange-btn px-4"
          :disabled="!canConfirm"
          @click="confirm"
        >
          <b-spinner v-if="submitting" small class="mr-1" />
          {{
            submitting
              ? "Guardando..."
              : `${messages.CONFIRM_IMPORT_BUTTON} (${validRows.length})`
          }}
        </b-button>
      </div>
    </div>

    <div v-else-if="step === 'result'">
      <p class="text-grey mb-2">{{ messages.IMPORT_STEP_RESULT_TITLE }}</p>

      <b-alert show :variant="resultVariant">
        {{ resultMessage }}
      </b-alert>

      <p v-if="invalidRows.length" class="text-muted small mb-1">
        {{ invalidRows.length }} {{ messages.IMPORT_INVALID_ROWS_LABEL }}
        {{ messages.IMPORT_NOT_SENT_SUFFIX }}
      </p>
      <p v-if="noEncontrados.length" class="text-muted small">
        {{ noEncontrados.length }} {{ messages.IMPORT_NOT_FOUND_SUFFIX }}
      </p>

      <div class="d-flex flex-wrap">
        <b-button
          v-if="reportRows.length"
          class="bg-grey-btn mr-2 mb-2"
          @click="downloadErrors"
        >
          <font-awesome-icon icon="file-excel" />
          {{ messages.DOWNLOAD_ERRORS_BUTTON }}
        </b-button>
        <b-button class="bg-grey-btn mr-2 mb-2" @click="reset">
          {{ messages.IMPORT_NEW_BUTTON }}
        </b-button>
        <b-button class="bg-orange-btn mb-2" @click="close">
          {{ messages.IMPORT_CLOSE_BUTTON }}
        </b-button>
      </div>
    </div>
  </b-modal>
</template>

<script>
import { MESSAGES } from "@/services/operaciones/operaciones.constants";
import {
  parseOperacionesImportRows,
  validateImportRow,
  buildGuardarOcMigoMasivoPayload,
  assertImportRowLimit,
  mapNoEncontradosToRows,
  applyValidationResult,
} from "@/services/operaciones/operaciones.contract";
import {
  downloadOperacionesImportTemplate,
  readExcelFileAsRows,
  exportImportErrorsToExcel,
} from "@/services/operaciones/operaciones.excel";
import {
  guardarOcMigoMasivo,
  validarOcMigoMasivo,
  getOperacionesErrorMessage,
} from "@/services/operaciones/operaciones.api";

const PREVIEW_FIELDS = [
  {
    key: "rowNumber",
    label: "Fila",
    tdClass: "text-center",
    thClass: "text-center",
  },
  { key: "ruc", label: "RUC" },
  { key: "tienda", label: "Tienda" },
  { key: "material", label: "Material" },
  { key: "numeroOc", label: "N° OC" },
  { key: "migo", label: "Migo" },
  {
    key: "error",
    label: "Estado",
    tdClass: "text-center",
    thClass: "text-center",
  },
  { key: "acciones", label: "" },
];

export default {
  name: "OperacionesImportWizard",

  props: {
    value: { type: Boolean, default: false },
    idPeriodo: { type: [Number, String], default: null },
    usuario: { type: String, default: "SISTEMA" },
  },

  data() {
    return {
      messages: MESSAGES,
      step: "select",
      fileName: "",
      rows: [],
      reading: false,
      readError: "",
      validating: false,
      validationError: "",
      submitting: false,
      submitError: "",
      result: null,
      noEncontrados: [],
      rowFilter: "all",
      currentPage: 1,
      perPage: 25,
      initialErrorRows: new Set(),
    };
  },

  computed: {
    visible: {
      get() {
        return this.value;
      },
      set(nextValue) {
        this.$emit("input", nextValue);
      },
    },
    previewFields() {
      return PREVIEW_FIELDS;
    },
    validRows() {
      return this.rows.filter((row) => !row.error);
    },
    invalidRows() {
      return this.rows.filter((row) => row.error);
    },
    canConfirm() {
      return this.validRows.length > 0 && !this.submitting && !this.validating;
    },
    resultMessage() {
      if (!this.result) return "";
      return (
        this.result.message ||
        `Se procesaron ${this.result.totalProcesados ??
          this.validRows.length} registros.`
      );
    },
    resultVariant() {
      if (!this.result || !this.result.success) return "danger";
      return this.noEncontrados.length ? "warning" : "success";
    },
    reportRows() {
      return [
        ...this.invalidRows.map((row) => ({ ...row, reason: row.error })),
        ...this.noEncontrados,
      ];
    },
    filteredRows() {
      if (this.rowFilter === "error") {
        return this.rows.filter((row) => this.initialErrorRows.has(row));
      }
      if (this.rowFilter === "valid") return this.validRows;
      return this.rows;
    },

    errorSummary() {
      const counts = new Map();
      this.invalidRows.forEach((row) => {
        counts.set(row.error, (counts.get(row.error) || 0) + 1);
      });
      return [...counts.entries()]
        .map(([message, count]) => ({ message, count }))
        .sort((a, b) => b.count - a.count);
    },
  },
  watch: {
    rowFilter() {
      this.currentPage = 1;
    },
    filteredRows(rows) {
      const lastPage = Math.max(1, Math.ceil(rows.length / this.perPage));
      if (this.currentPage > lastPage) this.currentPage = lastPage;
    },
  },
  methods: {
    downloadTemplate() {
      downloadOperacionesImportTemplate();
    },

    triggerFileSelect() {
      this.$refs.fileInput.click();
    },

    async onFileSelected(event) {
      const file = event.target.files && event.target.files[0];
      event.target.value = "";
      if (!file) return;

      this.readError = "";
      this.reading = true;
      try {
        const rawRows = await readExcelFileAsRows(file);
        const rows = parseOperacionesImportRows(rawRows);

        if (!rows.length) {
          this.readError = this.messages.IMPORT_NO_ITEMS;
          return;
        }

        assertImportRowLimit(rows);

        this.fileName = file.name;
        this.rows = rows;
        this.initialErrorRows = new Set(rows.filter((r) => r.error));
        this.rowFilter = rows.some((r) => r.error) ? "error" : "all";
        this.currentPage = 1;
        this.step = "preview";

        await this.validateRows();
      } catch (error) {
        console.error(error);
        this.readError =
          error.code === "IMPORT_ROW_LIMIT_EXCEEDED"
            ? error.message
            : this.messages.IMPORT_ERROR;
      } finally {
        this.reading = false;
      }
    },

    async validateRows() {
      const candidateRows = this.rows.filter((row) => !row.error);
      if (!candidateRows.length) {
        this.validationError =
          "Todas las filas tienen errores de formato. Corrige al menos una para poder validar contra el sistema.";
        return;
      }

      this.validationError = "";
      this.submitError = "";
      this.validating = true;
      try {
        const payload = buildGuardarOcMigoMasivoPayload(
          this.idPeriodo,
          this.rows,
          this.usuario,
        );
        const validation = await validarOcMigoMasivo(payload);
        applyValidationResult(this.rows, validation.noEncontrados);
        this.initialErrorRows = new Set(this.rows.filter((r) => r.error));
        if (this.initialErrorRows.size) {
          this.rowFilter = "error"; 
          this.currentPage = 1;
        }
      } catch (error) {
        console.error(error);
        this.validationError =
          getOperacionesErrorMessage(error) ||
          this.messages.IMPORT_VALIDATE_ERROR;
      } finally {
        this.validating = false;
      }
    },

    revalidate(row) {
      row.error = validateImportRow(row);
    },

    removeRow(row) {
      this.rows = this.rows.filter((item) => item !== row);
    },

    previewRowClass(item) {
      if (!item) return "";
      return item.error ? "table-danger" : "";
    },

    async confirm() {
      if (!this.canConfirm) return;

      this.submitting = true;
      this.submitError = "";
      this.validationError = "";
      try {
        const payload = buildGuardarOcMigoMasivoPayload(
          this.idPeriodo,
          this.rows,
          this.usuario,
        );
        const result = await guardarOcMigoMasivo(payload);
        this.result = result;
        this.noEncontrados = mapNoEncontradosToRows(
          result.noEncontrados,
          this.rows,
        );
        this.step = "result";
        this.$emit("success", result);
      } catch (error) {
        this.submitError =
          getOperacionesErrorMessage(error) || this.messages.IMPORT_ERROR;
      } finally {
        this.submitting = false;
      }
    },

    downloadErrors() {
      exportImportErrorsToExcel(this.reportRows);
    },

    reset() {
      this.step = "select";
      this.fileName = "";
      this.rows = [];
      this.readError = "";
      this.validationError = "";
      this.submitError = "";
      this.result = null;
      this.noEncontrados = [];
      this.rowFilter = "all";
      this.currentPage = 1;
      this.initialErrorRows = new Set();
    },

    close() {
      this.visible = false;
    },

    handleHide(bvModalEvt) {
      const isDismissTrigger =
        bvModalEvt.trigger === "backdrop" || bvModalEvt.trigger === "esc";
      const hasUnconfirmedWork =
        this.step === "preview" && this.rows.length > 0;

      if (isDismissTrigger && hasUnconfirmedWork) {
        const confirmLeave = window.confirm(
          this.messages.IMPORT_UNSAVED_CONFIRM,
        );
        if (!confirmLeave) {
          bvModalEvt.preventDefault();
        }
      }
    },

    handleHidden() {
      this.reset();
      this.$emit("hidden");
    },
  },
};
</script>

<style>
.operaciones-import-wizard__table-wrap {
  max-height: 400px;
  overflow-y: auto;
}
</style>
